import { Descriptions, Pagination, Table, Tooltip, Typography } from "antd";
import DataObjectLink from "components/atoms/DataObjectLink";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import FullHeightContainerLayout from "components/atoms/FullHeightContainerLayout";
import BatchProcessResult from "components/molecules/BatchProcessResult";
import { goldenEventAction, goldenEventDetails, goldenRecordTypeLabel } from "common/goldenEvents";
import EventApiKey from "containers/EventApiKey";
import EventBusinessArea from "containers/EventBusinessArea";
import EventBusinessAreaApiKeys from "containers/EventBusinessAreaApiKeys";
import EventBusinessAreaSystemConnections from "containers/EventBusinessAreaSystemConnections";
import EventBusinessAreaUsers from "containers/EventBusinessAreaUsers";
import EventDataDomain from "containers/EventDataDomain";
import EventDataObject from "containers/EventDataObject";
import EventDataObjectVerification from "containers/EventDataObjectVerification";
import EventDataSet from "containers/EventDataSet";
import EventDataTag from "containers/EventDataTag";
import EventDomainData from "containers/EventDomainData";
import EventJob from "containers/EventJob";
import EventRole from "containers/EventRole";
import EventSchemaModel from "containers/EventSchemaModel";
import EventSystemConnection from "containers/EventSystemConnection";
import EventSystemKeys from "containers/EventSystemKeys";
import EventUser from "containers/EventUser";
import moment from "moment";
import { useEffect, useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Column } = Table;
const { Link, Text, Title } = Typography;

const EventDataComponent = ({ eventId, recordType, ...props }) => {
    let component = <></>;
    switch (recordType) {
        case "DATA_DOMAIN":
            component = <EventDataDomain key={`${eventId}_${recordType}`} eventId={eventId} {...props}></EventDataDomain>;
            break;
        case "DOMAIN_DATA":
            component = <EventDomainData key={props.itemOperation ? `${props.itemOperation}-${eventId}` : eventId} eventId={eventId} {...props}></EventDomainData>;
            break;
        case "SCHEMA":
            component = <EventSchemaModel key={`${eventId}_${recordType}`} eventId={eventId} {...props}></EventSchemaModel>;
            break;
        case "USER":
            component = <EventUser key={`${eventId}_${recordType}`} eventId={eventId} {...props}></EventUser>;
            break;
        case "BUSINESS_AREA_USER":
            component = <EventBusinessAreaUsers key={props.itemOperation ? `${props.itemOperation}-${eventId}` : eventId} eventId={eventId} {...props}></EventBusinessAreaUsers>;
            break;
        case "BUSINESS_AREA_API_KEY":
        case "BUSINESS_AREA_APP_CLIENT":
            component = (
                <EventBusinessAreaApiKeys key={props.itemOperation ? `${props.itemOperation}-${eventId}` : eventId} eventId={eventId} {...props}></EventBusinessAreaApiKeys>
            );
            break;
        case "BUSINESS_AREA_SYSTEM_CONNECTION":
            component = (
                <EventBusinessAreaSystemConnections
                    key={props.itemOperation ? `${props.itemOperation}-${eventId}` : eventId}
                    eventId={eventId}
                    {...props}
                ></EventBusinessAreaSystemConnections>
            );
            break;
        case "BUSINESS_AREA":
            component = <EventBusinessArea key={`${eventId}_${recordType}`} eventId={eventId} {...props}></EventBusinessArea>;
            break;
        case "ROLE":
            component = <EventRole key={`${eventId}_${recordType}`} eventId={eventId} {...props}></EventRole>;
            break;
        case "DATA_SET":
            component = <EventDataSet key={`${eventId}_${recordType}`} eventId={eventId} {...props}></EventDataSet>;
            break;
        case "DATA_OBJECT":
            component = <EventDataObject key={props.itemOperation ? `${props.itemOperation}-${eventId}` : eventId} eventId={eventId} {...props}></EventDataObject>;
            break;
        case "DATA_TAG":
            component = <EventDataTag key={`${eventId}_${recordType}`} eventId={eventId} {...props}></EventDataTag>;
            break;
        case "API_KEY":
        case "APP_CLIENT":
            component = <EventApiKey key={`${eventId}_${recordType}`} eventId={eventId} {...props}></EventApiKey>;
            break;
        case "JOB":
            component = <EventJob key={`${eventId}_${recordType}`} eventId={eventId} {...props}></EventJob>;
            break;
        case "SYSTEM_CONNECTION":
            component = <EventSystemConnection key={`${eventId}_${recordType}`} eventId={eventId} {...props}></EventSystemConnection>;
            break;
        case "DATA_OBJECT_VERIFICATION":
            component = <EventDataObjectVerification key={`${eventId}_${recordType}`} eventId={eventId} {...props}></EventDataObjectVerification>;
            break;
        case "SYSTEM_INTEGRATION_KEY":
            component = <EventSystemKeys key={`${eventId}_${recordType}`} eventId={eventId} {...props}></EventSystemKeys>;
            break;
        case "ERROR":
            component = (
                <Descriptions
                    title="Error Details"
                    size={"small"}
                    column={1}
                    items={[
                        {
                            key: "1",
                            label: "Error Code",
                            children: props.errorCode || "N/A",
                        },
                        {
                            key: "2",
                            label: "Error Message",
                            children: props.errorMessage || "N/A",
                        },
                        {
                            key: "3",
                            label: "Log ID",
                            children: props.logId || "N/A",
                        },
                        {
                            key: "4",
                            label: "Request ID",
                            children: props.requestId || "N/A",
                        },
                    ]}
                />
            );
            break;
        default:
            break;
    }
    return <FullHeightContainerLayout content={component} showFooter={false}></FullHeightContainerLayout>;
};

const EventList = ({ eventList }) => {
    const [selectedRecordEvent, setSelectedRecordEvent] = useState(null);
    const [pagination, setPagination] = useState({ defaultPageSize: 100, pageSize: 100, page: 1 });
    const [paginatedEventList, setPaginatedEventList] = useState(eventList && eventList.slice(pagination.page - 1, pagination.pageSize));

    useEffect(() => {
        setPaginatedEventList(eventList && eventList.slice((pagination.page - 1) * pagination.pageSize, pagination.page * pagination.pageSize));
    }, [eventList, pagination.page, pagination.pageSize]);

    const onPageClick = (page, pageSize) => {
        let currentPagination = { ...pagination };
        let currentPaginatedEventList = eventList && eventList.slice((page - 1) * pageSize, page * pageSize);
        setPaginatedEventList(currentPaginatedEventList);
        currentPagination.page = page;
        currentPagination.pageSize = pageSize;
        setPagination(currentPagination);
    };

    return (
        <FlexContainerLayout contentClassName="gap-4">
            <FlexContainerLayout
                showFooter={eventList.length > pagination.pageSize}
                footerClassName="flex-none h-8"
                footer={
                    <Pagination
                        size="small"
                        hideOnSinglePage={true}
                        total={eventList.length}
                        defaultPageSize={pagination.defaultPageSize}
                        pageSize={pagination.pageSize}
                        responsive={true}
                        showSizeChanger={false}
                        onChange={onPageClick}
                    />
                }
            >
                <Table
                    size="small"
                    rowKey={(record) => `${record.eventId}_${record.recordType}`}
                    dataSource={paginatedEventList}
                    pagination={false}
                    className="flex size-full flex-1 flex-col [&_.error-row>td]:!border-inherit [&_.error-row>td]:![color:red] [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:bg-header-footer [&_thead]:text-header-footer"
                    rowClassName={(record) => (record.recordType === "ERROR" ? "error-row" : "")}
                    data-testid={CypressTestIds.COMMON_OBJECTS_EVENT_LIST_TABLE}
                >
                    <Column width="3rem" title="No." key="index" render={(value, item, index) => index + 1} />
                    <Column
                        width="8rem"
                        dataIndex={"createdDate"}
                        title={"Date"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, event, index) => {
                            let displayValue = null;
                            if (value) {
                                displayValue = moment.utc(value).toDate().toLocaleDateString();
                                return (
                                    <Tooltip placement="topLeft" title={displayValue}>
                                        {displayValue}
                                    </Tooltip>
                                );
                            }
                            return <></>;
                        }}
                    />
                    <Column
                        width="8rem"
                        dataIndex={"createdDate"}
                        title={"Time"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, event, index) => {
                            let displayValue = null;
                            if (value) {
                                displayValue = moment.utc(value).toDate().toLocaleTimeString();
                                return (
                                    <Tooltip placement="topLeft" title={displayValue}>
                                        {displayValue}
                                    </Tooltip>
                                );
                            }
                            return <></>;
                        }}
                    />
                    <Column
                        width="15rem"
                        dataIndex={"createdByEmail"}
                        title={"User"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, event, index) => {
                            if (event.recordType === "ERROR") {
                                return (
                                    <Tooltip placement="topLeft" title={"System"}>
                                        {"System"}
                                    </Tooltip>
                                );
                            }
                            return (
                                <Tooltip placement="topLeft" title={value}>
                                    {value}
                                </Tooltip>
                            );
                        }}
                    />
                    <Column
                        width="15rem"
                        dataIndex={"businessAreaName"}
                        title={"Business Area"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, event, index) => (
                            <Tooltip placement="topLeft" title={value || "<<none>>"}>
                                {value || "<<none>>"}
                            </Tooltip>
                        )}
                    />
                    <Column
                        width="20rem"
                        dataIndex={"recordType"}
                        title={"Record"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, event, index) => {
                            let displayValue = null;
                            if (value) {
                                switch (value) {
                                    case "DATA_DOMAIN":
                                        displayValue = "Business Area Data";
                                        break;
                                    case "DOMAIN_DATA":
                                        displayValue = "Domain";
                                        break;
                                    case "SCHEMA":
                                        displayValue = "Schema";
                                        break;
                                    case "USER":
                                        displayValue = "User";
                                        break;
                                    case "BUSINESS_AREA_USER":
                                        displayValue = "Business Area Users";
                                        break;
                                    case "BUSINESS_AREA_API_KEY":
                                    case "BUSINESS_AREA_APP_CLIENT":
                                        displayValue = "Business Area API Keys";
                                        break;
                                    case "BUSINESS_AREA_SYSTEM_CONNECTION":
                                        displayValue = "Business Area System Connections";
                                        break;
                                    case "BUSINESS_AREA":
                                        displayValue = "Business Area";
                                        break;
                                    case "ROLE":
                                        displayValue = "Roles";
                                        break;
                                    case "DATA_SET":
                                        displayValue = "Dataset";
                                        break;
                                    case "DATA_OBJECT":
                                        displayValue = "Dataset Object";
                                        break;
                                    case "DATA_TAG":
                                        displayValue = "Data Tag";
                                        break;
                                    case "API_KEY":
                                    case "APP_CLIENT":
                                        displayValue = "API Key";
                                        break;
                                    case "JOB":
                                        displayValue = "Job";
                                        break;
                                    case "SYSTEM_CONNECTION":
                                        if (event.connectionType === "aws_s3") {
                                            displayValue = "AWS S3 Connection";
                                        } else if (event.connectionType === "jdbc") {
                                            displayValue = "PostgreSQL Connection";
                                        } else if (event.connectionType === "snowflake") {
                                            displayValue = "Snowflake Connection";
                                        } else if (event.connectionType === "salesforce") {
                                            displayValue = "Salesforce Connection";
                                        } else if (event.connectionType === "hubspot") {
                                            displayValue = "HubSpot Connection";
                                        } else {
                                            displayValue = "System Connection";
                                        }
                                        break;
                                    case "SYSTEM_INTEGRATION_KEY":
                                        displayValue = "System Key";
                                        break;
                                    case "DATA_OBJECT_VERIFICATION":
                                        displayValue = "Record verification";
                                        break;
                                    case "ERROR":
                                        displayValue = "System Error";
                                        break;
                                    default:
                                        displayValue = goldenRecordTypeLabel(value) || <></>;
                                }
                                return (
                                    <Tooltip placement="topLeft" title={displayValue}>
                                        {displayValue}
                                    </Tooltip>
                                );
                            }
                            return <></>;
                        }}
                    />
                    <Column
                        width="15rem"
                        dataIndex={"recordType"}
                        title={"Record ID"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, event, index) => {
                            let displayValue = null;
                            if (value) {
                                switch (value) {
                                    case "DATA_DOMAIN":
                                        displayValue = event.domainName;
                                        break;
                                    case "SCHEMA":
                                        displayValue = event.schemaName;
                                        break;
                                    case "DOMAIN_DATA":
                                        displayValue = event.batchProcessing ? (
                                            <Text type="secondary" italic="true" style={{ maxWidth: "12rem" }}>{`<<multiple records>>`}</Text>
                                        ) : (
                                            <></>
                                        );
                                        break;
                                    case "USER":
                                        displayValue = event.userEmail;
                                        break;
                                    case "BUSINESS_AREA_USER":
                                        displayValue = event.batchProcessing ? (
                                            <Text type="secondary" italic="true" style={{ maxWidth: "12rem" }}>{`<<multiple records>>`}</Text>
                                        ) : (
                                            <>{event.businessAreaUserEmail}</>
                                        );
                                        break;
                                    case "BUSINESS_AREA_API_KEY":
                                    case "BUSINESS_AREA_APP_CLIENT":
                                        displayValue = event.batchProcessing ? (
                                            <Text type="secondary" italic="true" style={{ maxWidth: "12rem" }}>{`<<multiple records>>`}</Text>
                                        ) : (
                                            <>{event.clientName}</>
                                        );
                                        break;
                                    case "BUSINESS_AREA_SYSTEM_CONNECTION":
                                        displayValue = event.batchProcessing ? (
                                            <Text type="secondary" italic="true" style={{ maxWidth: "12rem" }}>{`<<multiple records>>`}</Text>
                                        ) : (
                                            <>{event.connectionName}</>
                                        );
                                        break;
                                    case "BUSINESS_AREA":
                                        displayValue = event.businessAreaName;
                                        break;
                                    case "ROLE":
                                        displayValue = event.roleName;
                                        break;
                                    case "DATA_SET":
                                        displayValue = event.dataSetName;
                                        break;
                                    case "DATA_OBJECT":
                                        displayValue = event.batchProcessing ? (
                                            <Text type="secondary" italic="true" style={{ maxWidth: "12rem" }}>{`<<multiple records>>`}</Text>
                                        ) : (
                                            <>
                                                <DataObjectLink dataObjectId={event.dataObjectId}></DataObjectLink>
                                            </>
                                        );
                                        break;
                                    case "DATA_TAG":
                                        displayValue = event.tagName;
                                        break;
                                    case "API_KEY":
                                    case "APP_CLIENT":
                                        displayValue = event.apiKeyName || event.name || event.clientName;
                                        break;
                                    case "JOB":
                                        displayValue = event.jobId;
                                        break;
                                    case "SYSTEM_CONNECTION":
                                        displayValue = event.connectionName;
                                        break;
                                    case "DATA_OBJECT_VERIFICATION":
                                        displayValue = event.verificationId;
                                        break;
                                    case "SYSTEM_INTEGRATION_KEY":
                                        displayValue = event.keyId;
                                        break;
                                    case "ERROR":
                                        displayValue = event.processId;
                                        break;
                                    default:
                                        displayValue = <></>;
                                }
                                return (
                                    <Tooltip placement="topLeft" title={displayValue}>
                                        {displayValue}
                                    </Tooltip>
                                );
                            }
                            return <></>;
                        }}
                    />
                    <Column
                        width="12rem"
                        dataIndex={"recordType"}
                        title={"Record Parent"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, event, index) => {
                            let displayValue = null;
                            if (value) {
                                switch (value) {
                                    case "DOMAIN_DATA":
                                        displayValue = event.domainName;
                                        break;
                                    case "BUSINESS_AREA_USER":
                                    case "BUSINESS_AREA_API_KEY":
                                    case "BUSINESS_AREA_APP_CLIENT":
                                    case "BUSINESS_AREA_SYSTEM_CONNECTION":
                                        displayValue = event.businessAreaName;
                                        break;
                                    case "DATA_SET":
                                        displayValue = event.schemaName;
                                        break;
                                    case "DATA_OBJECT":
                                        displayValue = event.dataSetName ? event.dataSetName : <>{"<<none>>"}</>;
                                        break;
                                    case "DATA_OBJECT_VERIFICATION":
                                        displayValue = event.dataObjectId ? <DataObjectLink dataObjectId={event.dataObjectId}></DataObjectLink> : <>{"<<none>>"}</>;
                                        break;
                                    default:
                                        displayValue = <>{"<<none>>"}</>;
                                }
                                return (
                                    <Tooltip placement="topLeft" title={displayValue}>
                                        {displayValue}
                                    </Tooltip>
                                );
                            }
                            return <></>;
                        }}
                    />
                    <Column
                        width="12rem"
                        dataIndex={"eventType"}
                        title={"Action"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, event, index) => {
                            let displayValue = null;
                            if (value) {
                                switch (value) {
                                    case "CREATED_RECORD":
                                        displayValue = "Created";
                                        break;
                                    case "UPDATED_RECORD":
                                        displayValue = "Updated";
                                        break;
                                    case "DELETED_RECORD":
                                        displayValue = "Deleted";
                                        break;
                                    case "BATCH_PROCESSING":
                                        displayValue = "Bulk data processed";
                                        break;
                                    case "DELETED_DUPLICATE_RECORD":
                                        displayValue = "Duplicate record deleted";
                                        break;
                                    // Golden Nominal maintenance: deleted, keeping which record it was merged into.
                                    case "RETIRED_DUPLICATE_RECORD":
                                        displayValue = "Duplicate record retired";
                                        break;
                                    case "GET_UNMASKED_RECORD":
                                        displayValue = "Viewed";
                                        break;
                                    case "ERROR":
                                        displayValue = "Unexpected Error";
                                        break;
                                    default:
                                        displayValue = goldenEventAction(event) || <></>;
                                }
                                return (
                                    <Tooltip placement="topLeft" title={displayValue}>
                                        {displayValue}
                                    </Tooltip>
                                );
                            }
                            return <></>;
                        }}
                    />
                    <Column
                        width="12rem"
                        dataIndex={"details"}
                        title={"Details"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, event, index) => {
                            let displayValue = null;
                            switch (event.eventType) {
                                case "CREATED_RECORD":
                                case "UPDATED_RECORD":
                                case "DELETED_RECORD":
                                case "DELETED_DUPLICATE_RECORD":
                                case "RETIRED_DUPLICATE_RECORD":
                                    displayValue = (
                                        <Link
                                            ellipsis={true}
                                            style={{ maxWidth: "12rem" }}
                                            onClick={() => {
                                                setSelectedRecordEvent(event);
                                            }}
                                        >
                                            1 Record
                                        </Link>
                                    );
                                    break;
                                case "GET_UNMASKED_RECORD":
                                    displayValue = (
                                        <Link
                                            ellipsis={true}
                                            style={{ maxWidth: "12rem" }}
                                            onClick={() => {
                                                setSelectedRecordEvent(event);
                                            }}
                                        >
                                            1 Record
                                        </Link>
                                    );
                                    break;
                                case "BATCH_PROCESSING":
                                    let batchProcessResult = value;
                                    if (
                                        value &&
                                        (event.recordType === "BUSINESS_AREA_USER" ||
                                            event.recordType === "BUSINESS_AREA_API_KEY" ||
                                            event.recordType === "BUSINESS_AREA_APP_CLIENT" ||
                                            event.recordType === "BUSINESS_AREA_SYSTEM_CONNECTION")
                                    ) {
                                        batchProcessResult = {
                                            error: null,
                                            itemOperations: value,
                                        };
                                    }
                                    displayValue = batchProcessResult ? (
                                        <BatchProcessResult
                                            batchProcessResult={batchProcessResult}
                                            textMaxWidth="12rem"
                                            onBatchOperationClick={(item) => {
                                                setSelectedRecordEvent({ ...event, ...item });
                                            }}
                                        ></BatchProcessResult>
                                    ) : (
                                        <></>
                                    );
                                    break;
                                case "ERROR":
                                    displayValue = (
                                        <Link
                                            ellipsis={true}
                                            style={{ maxWidth: "12rem" }}
                                            onClick={() => {
                                                setSelectedRecordEvent(event);
                                            }}
                                        >
                                            Show Error
                                        </Link>
                                    );
                                    break;
                                default: {
                                    const goldenDetails = goldenEventDetails(event);
                                    displayValue = goldenDetails ? (
                                        <Tooltip placement="topLeft" title={goldenDetails}>
                                            {goldenDetails}
                                        </Tooltip>
                                    ) : (
                                        <></>
                                    );
                                }
                            }
                            return displayValue;
                        }}
                    />
                </Table>
            </FlexContainerLayout>
            {selectedRecordEvent ? (
                <FlexContainerLayout>
                    <EventDataComponent eventId={selectedRecordEvent.eventId} recordType={selectedRecordEvent.recordType} {...selectedRecordEvent}></EventDataComponent>
                </FlexContainerLayout>
            ) : (
                <></>
            )}
        </FlexContainerLayout>
    );
};

export default EventList;
